/* eslint-disable camelcase */
import { useLocale } from '@/components/Link'
import type { FragmentType } from '@/generated/gql'
import { graphql, useFragment } from '@/generated/gql'
import type { OrderBasketMapping_OrderDataFragmentFragment } from '@/generated/gql/graphql'
import type { BasketFragment, GmMenuStandingOrderDataFragment } from '@/generated/graphql'
import { getPriceFormatter } from '@/helpers/currency'
import { getCustomerFacingPriceItem } from '@/helpers/customerPricing/customerPricing'
import { numberFormat } from '@/helpers/numbers'
import { validateBasketItemUpdate } from '@caterdesk/utils--gm-validation'

import { useMemo } from 'react'
import { match, P } from 'ts-pattern'

import type { ConsolidatedBasketProps } from '.'
import { CustomerFacingExistingGmOrderItem } from '../../states/useExistingOrder/useExistingOrderItems'
import { Fees, TaxRateTotals } from '../../states/useExistingOrder/useInputRelatedVariableOrderData'

const orderDataFragment = graphql(`
  fragment OrderBasketMapping_OrderDataFragment on GmOrder {
    id
    orderId
    orderItems {
      items {
        id
        name
        priceExTax
        qty
        note
        orderItemOptions {
          name
          priceExTax
          optionType
        }
      }
    }
    headCount
    subtotalExTax
    orderTotalBreakdown {
      taxRateTotals {
        rate
        totalExTax
      }
      totalExTax
    }
    standingOrder {
      friendlyId
    }
    fees {
      code
      totalExTax
      description
    }
  }
`)

type Mode = 'basket' | 'standingorder' | 'placedorder'

type BasketDataRequired = Pick<
  BasketFragment,
  | 'id'
  | 'items'
  | 'customItems'
  | 'fees'
  | 'basketTotalBreakdown'
  | 'subtotalBreakdown'
  | 'settings'
  | 'friendlyId'
>

type MatchInput = {
  basketData?: BasketDataRequired | null
  orderData?: OrderBasketMapping_OrderDataFragmentFragment | null
  standingOrderData?: GmMenuStandingOrderDataFragment | null
}

type GrandTotals = {
  totalIncTax?: string
  totalTax?: string
}

const sanitiseAmount = (amount: string | number): number => {
  if (typeof amount === 'string') return Number(amount.replaceAll(',', '')) || 0
  return Number.isFinite(amount) ? amount : 0
}

const calculateAndFormatPrice = (
  amount: string | number,
  quantity: number,
  locale: string,
): string => {
  const formatter = getPriceFormatter(locale)
  const sanitisedAmount = sanitiseAmount(amount)

  const totalAmount = quantity > 0 ? sanitisedAmount * quantity : sanitisedAmount

  return formatter.format(totalAmount)
}

// Only the adder may change the qty of an attributed line: a bump by anyone else would
// misattribute the extra on the vendor's labels.
const isAttributedToAnotherUser = (
  addedByUserId: number | null | undefined,
  isCateringTeamOrder: boolean,
  currentUserId: string | undefined,
) => isCateringTeamOrder && addedByUserId != null && String(addedByUserId) !== currentUserId

export const useMappedBasketOrOrderDataToCartData = (input: {
  basketData?: BasketDataRequired | null
  orderData: {
    rawData: FragmentType<typeof orderDataFragment> | null
    fees: Fees
    grandTotals: GrandTotals
    taxRateTotals: TaxRateTotals
    itemsState: CustomerFacingExistingGmOrderItem[]
  }
  standingOrderData?: GmMenuStandingOrderDataFragment | null
  /** Team-order context: surfaces attribution and locks quantity on lines the viewer didn't add. */
  cateringTeamOrder?: {
    isCateringTeamOrder: boolean
    currentUserId?: string
    isContributor?: boolean
  }
}): {
  mode: Mode
} & Pick<ConsolidatedBasketProps, 'items' | 'fees' | 'taxRates' | 'summary' | 'id'> => {
  const { basketData, standingOrderData } = input
  const isCateringTeamOrder = input.cateringTeamOrder?.isCateringTeamOrder ?? false
  const currentUserId = input.cateringTeamOrder?.currentUserId
  const isContributor = input.cateringTeamOrder?.isContributor ?? false

  const orderData = useFragment(orderDataFragment, input.orderData.rawData)

  const locale = useLocale() ?? ''

  const mode = useMemo(() => {
    return match<MatchInput, Mode>({ basketData, orderData })
      .with({ orderData: P.not(P.nullish) }, () => 'placedorder')
      .with({ basketData: P.not(P.nullish) }, () => 'basket')
      .otherwise(() => 'standingorder')
  }, [basketData, orderData])

  const items = useMemo(() => {
    return match<MatchInput, ConsolidatedBasketProps['items']>({ basketData, orderData })
      .with({ orderData: P.not(P.nullish) }, () =>
        input.orderData.itemsState.map((item) => {
          // The owner is the only editor of a placed order, so delete stays open.
          const attributedToAnotherUser = isAttributedToAnotherUser(
            item.addedByUserId,
            isCateringTeamOrder,
            currentUserId,
          )
          return {
            id: item.id,
            coreItemId: item.liveItemId,
            minimumQuantity: item.minimumQuantity ?? 1,
            name: item.name,
            note: item.note ?? '',
            price: calculateAndFormatPrice(item.priceExTax, item.qty, locale),
            quantity: item.qty,
            canDelete: !item.isCustom,
            canUpdateQty: !item.isCustom && !attributedToAnotherUser,
            qtyLockedForCateringTeamOrder: attributedToAnotherUser,
            addedByUserId: isCateringTeamOrder ? item.addedByUserId : undefined,
            addedByName: isCateringTeamOrder ? item.addedByName : undefined,
            itemOptions:
              item.options.map((itemOption) => {
                return {
                  name: itemOption.name,
                  qty: itemOption.qty ?? 0,
                  type: itemOption.optionType,
                  id: itemOption.id,
                  priceExTax: calculateAndFormatPrice(
                    itemOption.priceExTax,
                    itemOption.qty,
                    locale,
                  ),
                }
              }) ?? [],
          }
        }),
      )
      .otherwise((data) => {
        const items =
          data.basketData?.items.map((item) => {
            // Contributors may delete only their own and unattributed lines — the owner may
            // delete anything (PT-1820).
            const attributedToAnotherUser = isAttributedToAnotherUser(
              item.addedByUserId,
              isCateringTeamOrder,
              currentUserId,
            )
            return {
              id: item.itemReference,
              coreItemId: item.item?._id,
              minimumQuantity: item.item?.variations?.[0]?.minQty || 0,
              name: item.item?.name || '',
              note: item.note ?? '',
              price: calculateAndFormatPrice(
                (getCustomerFacingPriceItem(item.item?.variations?.[0]?.customerPriceItems)
                  ?.priceExTax ?? 0) / 100,
                item.qty,
                locale,
              ),
              canDelete: !(isContributor && attributedToAnotherUser),
              canUpdateQty: !attributedToAnotherUser,
              qtyLockedForCateringTeamOrder: attributedToAnotherUser,
              quantity: item.qty,
              addedByUserId: isCateringTeamOrder ? item.addedByUserId : undefined,
              addedByName: isCateringTeamOrder ? item.addedByName : undefined,
              acknowledgedAt: item.acknowledgedAt,
              updatedAt: item.item?.updatedAt,
              itemOptions: item.options.map((itemOption) => {
                return {
                  name: itemOption.item?.name ?? '',
                  qty: itemOption.qty,
                  type: itemOption.category?.optionType ?? '',
                  id: itemOption.item?._id ?? '',
                  priceExTax: calculateAndFormatPrice(
                    (getCustomerFacingPriceItem(itemOption.optionItem?.customerPriceItems)
                      ?.priceExTax ?? 0) / 100,
                    itemOption.qty,
                    locale,
                  ),
                }
              }),
              updatedByVendor:
                validateBasketItemUpdate({
                  item: { ...item, updatedAt: item.item?.updatedAt ?? '' },
                  acknowledgedAt: item.acknowledgedAt ?? null,
                  options: item.options.map((_) => ({
                    item: { updatedAt: item.item?.updatedAt ?? '' },
                  })),
                }) || undefined,
            }
          }) ?? []

        const customItems =
          data.basketData?.customItems.map((item) => ({
            id: item.id,
            minimumQuantity: 0,
            name: item.name,
            note: item.notes ?? '',
            price: calculateAndFormatPrice(item.customerPriceExTax / 100, item.quantity, locale),
            quantity: item.quantity,
            acknowledgedAt: '',
            updatedAt: '',
            itemOptions: [],
            canDelete: false,
            canUpdateQty: false,
            updatedByVendor: false,
            isCustomItem: true,
          })) ?? []

        return [...items, ...customItems]
      })
  }, [
    basketData,
    orderData,
    locale,
    input.orderData.itemsState,
    isCateringTeamOrder,
    currentUserId,
    isContributor,
  ])

  const fees = match<MatchInput, ConsolidatedBasketProps['fees']>({
    basketData,
    orderData,
  })
    .with({ orderData: P.not(P.nullish) }, () =>
      input.orderData.fees
        .filter((fee) => Number(fee.totalExTax) !== 0)
        .map((fee) => {
          return {
            code: fee.code,
            description: fee.description,
            feeTotalExTax: calculateAndFormatPrice(fee.totalExTax, 0, locale),
          }
        }),
    )
    .otherwise(
      (data) =>
        data.basketData?.fees
          .filter((fee) => Number(fee.totalExTax) !== 0)
          .map((fee) => ({
            code: fee.code,
            description: fee.description,
            feeTotalExTax: calculateAndFormatPrice(fee.totalExTax / 100, 0, locale),
          })) ?? [],
    )

  const taxRates = match<MatchInput, ConsolidatedBasketProps['taxRates']>({
    basketData,
    orderData,
  })
    .with({ orderData: P.not(P.nullish) }, () =>
      input.orderData.taxRateTotals.map((tax) => {
        return {
          rate: numberFormat(Number(tax.rate)),
          totalExTax: calculateAndFormatPrice(tax.value, 1, locale),
        }
      }),
    )
    .otherwise(
      (data) =>
        data.basketData?.basketTotalBreakdown?.taxRateTotals?.map((tax) => ({
          rate: numberFormat(tax.rate * 100),
          totalExTax: calculateAndFormatPrice(tax.totalExTax / 100, 1, locale),
        })) ?? [],
    )

  const itemCount = items.reduce((total, item) => total + item.quantity, 0)

  const commonSummaryProperties = {
    charityContributionHeadcount:
      orderData?.headCount ?? basketData?.settings.headCount.toString() ?? '',
    friendlyId: standingOrderData?.name ?? basketData?.friendlyId ?? orderData?.orderId ?? '',
    subtotalExTax: calculateAndFormatPrice(
      sanitiseAmount(orderData?.subtotalExTax ?? basketData?.subtotalBreakdown.subtotalExTax ?? 0) /
        100,
      0,
      locale,
    ),
    totalExTax: calculateAndFormatPrice(
      sanitiseAmount(
        orderData?.orderTotalBreakdown.totalExTax ??
          basketData?.basketTotalBreakdown.totalExTax ??
          0,
      ) / 100,
      0,
      locale,
    ),
    totalIncTax: calculateAndFormatPrice(
      sanitiseAmount(input.orderData.grandTotals.totalIncTax ?? 0) / 100,
      0,
      locale,
    ),
    itemCount,
  }

  const summary: ConsolidatedBasketProps['summary'] =
    mode === 'placedorder'
      ? {
          ...commonSummaryProperties,
          totalTaxValue: calculateAndFormatPrice(
            input.orderData.grandTotals.totalTax ?? 0,
            0,
            locale,
          ),
          grandTotal: calculateAndFormatPrice(
            input.orderData.grandTotals.totalIncTax ?? 0,
            0,
            locale,
          ),
        }
      : {
          ...commonSummaryProperties,
          totalTaxValue: calculateAndFormatPrice(
            Number(basketData?.basketTotalBreakdown.taxValue) / 100,
            0,
            locale,
          ),
          grandTotal: calculateAndFormatPrice(
            Number(basketData?.basketTotalBreakdown.totalIncTax) / 100,
            0,
            locale,
          ),
        }

  const id = match<MatchInput>({
    basketData,
    orderData,
    standingOrderData,
  })
    .with({ orderData: P.not(P.nullish) }, ({ orderData }) => orderData.id)
    .with(
      { basketData: P.not(P.nullish), standingOrderData: P.nullish },
      ({ basketData }) => basketData.id,
    )
    .with({ standingOrderData: P.not(P.nullish) }, ({ standingOrderData }) => standingOrderData._id)
    .otherwise(() => '')

  return { items, mode, fees, summary, taxRates, id }
}
